import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Autocomplete, CircularProgress, FormControl, TextField } from '@mui/material'
import { FC } from 'react'
import ChipButton from '../ChipButton'
import { transform } from '../../../Helpers/Util'

const MuiAutocompleteWithoutControl: FC<any> = ({
  options = [],
  error,
  label,
  name = '',
  labelKey = 'label',
  valueKey = 'value',
  required = false,
  disabled = false,
  defaultValueIndex = Number.NaN,
  startIcon,
  searchOnly,
  placeholder,
  onChange,
  value,
  id,
  disabledOptions = [],
  statusKey,
  statusLabel,
  transformToBoolean = false,
  isOptionsLoading = false,
}) => {
  const formControlStyle: {
    textAlign: string
    position: string
    backgroundColor?: string
    borderRadius?: string
  } = { textAlign: 'left', position: 'relative' }

  if (name === 'parameterSearch') {
    formControlStyle['backgroundColor'] = 'white'
    formControlStyle['borderRadius'] = '5px'
  }

  const textFieldStyle: {
    '& .MuiInputBase-root'?: { fontWeight?: string; borderRadius?: string }
  } = {}

  if (value && searchOnly) {
    textFieldStyle['& .MuiInputBase-root'] = {
      fontWeight: '600',
    }
  }

  if (name === 'parameterSearch') {
    textFieldStyle['& .MuiInputBase-root'] = {
      ...textFieldStyle['& .MuiInputBase-root'],
      borderRadius: formControlStyle.borderRadius,
    }
  }

  const selectedValue =
    options.find((opt: any) => {
      const optVal = opt[valueKey]
      if (transformToBoolean) {
        return (
          transform(optVal) === transform(value) || transform(optVal) === transform(value?.[0]?.key)
        )
      }
      return (
        optVal === value ||
        optVal === value?.[0]?.key ||
        (value && typeof value === 'object' && optVal === value[valueKey])
      )
    }) || null

  return (
    <FormControl
      className='c-select'
      fullWidth
      data-testid='select-control'
      error={!!error?.[name]}
      sx={formControlStyle}
    >
      <Autocomplete
        id={id || label}
        data-testid={id || label}
        sx={{ '& .MuiInputBase-root': { padding: '5px' } }}
        options={options}
        loading={false}
        loadingText={null}
        noOptionsText={
          isOptionsLoading ? (
            <span
              data-testid='options-loading'
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <CircularProgress size={20} />
            </span>
          ) : (
            <span data-testid='no-options'>No options</span>
          )
        }
        getOptionLabel={(option: any) => option[labelKey] || ''}
        getOptionDisabled={(option: any) =>
          !!option.disabled ||
          disabledOptions?.includes(option[valueKey]) ||
          disabledOptions?.some?.(
            (disabledItem: any) => disabledItem?.[valueKey] === option?.[valueKey],
          )
        }
        isOptionEqualToValue={(option: any, val: any) => {
          if (transformToBoolean) {
            return transform(option[valueKey]) === transform(val[valueKey])
          }
          return option[valueKey] === val[valueKey]
        }}
        value={selectedValue}
        onChange={(_event, newValue) => {
          let selectedOption = newValue ? newValue[valueKey] : ''
          if (transformToBoolean) {
            selectedOption = transform(selectedOption)
          }
          if (onChange) {
            onChange(selectedOption, newValue)
          }
        }}
        ListboxProps={{
          style: searchOnly
            ? {
                maxHeight: '200px',
                padding: '0',
              }
            : {},
        }}
        componentsProps={{
          popper: {
            sx: searchOnly
              ? {
                  '& .MuiPaper-root': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    borderRadius: '8px',
                    marginTop: '8px',
                  },
                  '& .MuiAutocomplete-option': {
                    fontSize: '13px',
                    fontWeight: '600',
                    padding: '10px 16px',
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    },
                  },
                }
              : {},
          },
        }}
        renderOption={(props, option: any) => {
          const statusValue = statusKey ? option?.[statusKey] : undefined
          const isInactive =
            typeof statusValue === 'string' && statusValue.toUpperCase() === 'INACTIVE'
          return (
            <li
              {...props}
              style={{
                ...(searchOnly ? { padding: '8px 16px', fontSize: '14px' } : {}),
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>{option?.[labelKey] ?? option}</span>
              {statusValue !== undefined && (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginLeft: 8,
                    flexShrink: 0,
                  }}
                >
                  {statusLabel && (
                    <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>{statusLabel} •</span>
                  )}
                  <ChipButton
                    type={isInactive ? 'secondary' : 'success'}
                    label={isInactive ? 'Inactive' : 'Active'}
                    style={{ padding: '0px 8px' }}
                  />
                </span>
              )}
            </li>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            label={
              searchOnly ? null : (
                <span>
                  {label}
                  {required && <span style={{ color: '#D4351C' }}>*</span>}
                </span>
              )
            }
            error={!!error?.[name]}
            helperText={error?.[name]?.message as string}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: true,
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {startIcon}
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            sx={textFieldStyle}
            defaultValue={options?.length > 0 ? options?.[defaultValueIndex]?.value : ''}
          />
        )}
        disabled={disabled}
        popupIcon={searchOnly ? null : <KeyboardArrowDownIcon />}
      />
    </FormControl>
  )
}

export default MuiAutocompleteWithoutControl
